import { Badge, Tooltip } from 'antd'

import CheckboxIcon from '@Icons/checkbox-base.svg?react'
import ChevronRight from '@Icons/chevron-right.svg?react'

interface SelectionCardProps {
  onClick?: () => void
  isActive?: boolean
  title?: React.ReactNode
  titleElement?: React.ReactNode
  description?: React.ReactNode
  descriptionElement?: React.ReactNode
  icon?: React.ReactNode
  linkButton?: React.ReactNode
  linkButtonLabel?: React.ReactNode
  onLinkButtonClick?: (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void
  showCheckbox?: boolean
  disabled?: boolean
  /** Shown on hover when the card is disabled (explains why). */
  disabledTooltip?: string
  showBadge?: boolean
  className?: string
}

function SelectionCard({
  onClick = () => {},
  isActive,
  title,
  titleElement,
  description,
  descriptionElement,
  icon,
  linkButton,
  linkButtonLabel,
  showCheckbox = true,
  disabled = false,
  disabledTooltip,
  showBadge = false,
  onLinkButtonClick = () => {},
  className = '',
}: SelectionCardProps): JSX.Element {
  const card = (
    <div
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation()
        if (disabled) return
        onClick()
      }}
      className={`min-h-[120px] border border-solid border-gray-200 rounded-lg h-full flex flex-col justify-between transition-all duration-200 select-none pt-4 pb-6 px-6${isActive ? ' border-primary-600' : ''}${disabled ? ' opacity-60 cursor-not-allowed' : ' cursor-pointer'}${className && ` ${className}`}`}
    >
      <div className="absolute top-6 right-6">
        {showCheckbox &&
          (isActive ? (
            <CheckboxIcon
              style={{
                boxShadow: '0 0 0 4px #EEEFF7',
                borderRadius: '50%',
              }}
            />
          ) : (
            <div className="border border-solid border-gray-300 rounded-full w-5 h-5"></div>
          ))}
      </div>
      <div className="flex flex-col flex-1 justify-start">
        {titleElement ? (
          titleElement
        ) : (
          <h2 className="text-primary-800 text-base font-semibold leading-8 mb-2 flex flex-row justify-start items-center">
            <span>{icon}</span>
            {title}
          </h2>
        )}
        {descriptionElement ? (
          descriptionElement
        ) : (
          <p
            className={`font-normal text-base text-start ${isActive ? 'text-primary-600' : 'text-gray-500'}`}
          >
            {description}
          </p>
        )}
        {linkButton ? (
          linkButton
        ) : linkButtonLabel ? (
          <button
            disabled={disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation()
              if (disabled) return
              onLinkButtonClick()
            }}
            className="w-fit flex flex-row justify-start items-center [&>svg]:transition-all [&>svg]:ml-1 [&>svg]:h-6 [&>svg]:w-6 [&>svg]:hover:ml-[-2px] [&>svg>path]:stroke-primary-700 mt-2 text-primary-700 disabled:cursor-not-allowed"
          >
            {linkButtonLabel} <ChevronRight />
          </button>
        ) : (
          <></>
        )}
      </div>
    </div>
  )

  return disabled && disabledTooltip ? (
    <Tooltip title={disabledTooltip}>{card}</Tooltip>
  ) : (
    card
  )
}

function SelectionCardWithBadge(props: SelectionCardProps) {
  if (!props.showBadge) {
    return <SelectionCard {...props} />
  }

  return (
    <Badge.Ribbon
      text="Coming Soon"
      className="!inline-block !px-4 !py-1 top-4 [&>span]:text-xs [&>span]:font-medium bg-primary-600 text-white"
    >
      <SelectionCard {...props} showBadge={true} disabled={true} />
    </Badge.Ribbon>
  )
}

export default SelectionCardWithBadge
